/**
 * Design tokens for the PWA — mirror of the iOS design system.
 *
 * Spacing / radii / shadows / typography. Reference these instead of
 * hardcoding pixel literals so the app stays modular and responsive.
 *
 * Typography note: the responsive font scale already lives in
 * `client/src/App.css` as `--base-font-size*` CSS vars (these track iOS's
 * screen-height-based scaling), surfaced through Tailwind's `fontSize`
 * (text-xs..text-7xl) and `fontSize.var(...)`. Use those Tailwind classes or
 * the `fontVar()` helper below; don't introduce new px font sizes.
 */

/**
 * Canonical phone-column width (px) for every PWA parity surface. iOS only
 * renders at phone aspect; on wide/desktop the content is centered in this
 * column with the screen background full-bleed behind it. Reference this token
 * instead of re-declaring 600 (max-w-[600px], COLUMN_WIDTH, FS_MAX_WIDTH, …).
 */
export const pageMaxWidth = 600;

/** 4px base spacing scale (matches RN's common 4/8 rhythm used across iOS). */
export const space = {
  0: 0,
  0.5: 2,
  1: 4,
  1.5: 6,
  2: 8,
  2.5: 10,
  3: 12,
  3.5: 14,
  4: 16,
  5: 20,
  6: 24,
  7: 28,
  8: 32,
  10: 40,
  12: 48,
  14: 56,
  16: 64,
  20: 80,
} as const;

/**
 * Soft elevation shadows comparable to iOS card shadows. The `card` / `sm`
 * tokens route through the CSS vars in variables.css (ported from iOS Card
 * variants) so JS and CSS consumers can't drift to conflicting values.
 */
export const shadows = {
  none: "none",
  sm: "var(--shadow-badge)",
  card: "var(--shadow-card)",
  raised: "0 4px 16px rgba(0,0,0,0.12)",
  modal: "0 8px 32px rgba(0,0,0,0.18)",
  // 3D button bottom-edge shadow (iOS Button3D look)
  button3d: "0 4px 0 rgba(0,0,0,0.18)",
} as const;

/**
 * Stacking scale for fixed-position overlays so layering stays consistent and
 * intentional instead of scattered magic numbers. Higher = closer to the user.
 *   sheet   — bottom sheets / centered modals (LevelsSheet)
 *   overlay — generic full-screen scrims
 *   popup   — celebration popups layered above sheets
 *   modal   — top-most celebrations (LevelUpOverlay)
 */
export const zIndex = {
  sheet: 1000,
  overlay: 1025,
  popup: 1050,
  modal: 1100,
} as const;

/** CSS var reference for a font size when you need it inline. */
export function fontVar(
  variant:
    | "xxs"
    | "xs"
    | "small"
    | "base"
    | "large"
    | "xlarge"
    | "2xlarge"
    | "3xlarge"
    | "4xlarge"
    | "5xlarge"
    | "6xlarge"
    | "7xlarge"
): string {
  return variant === "base"
    ? "var(--base-font-size)"
    : `var(--base-font-size-${variant})`;
}

export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/**
 * Single source for the app font family. iOS renders all design-system text
 * in the system font (fontFamily "System" in client-native Text.tsx → SF on
 * iOS), so the web equivalent is the platform system stack — on iOS Safari
 * this resolves to the exact same San Francisco face. The previous "Inter"
 * declaration was never loaded and fell back to generic sans-serif.
 */
export const fontFamily =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const tokens = { pageMaxWidth, space, shadows, zIndex, fontWeight, fontFamily };
export default tokens;
